"use client";

import { MessageGenerationStage } from "./message-generation-stage";
import { Tooltip, TooltipProvider } from "./tooltip";
import { cn } from "../lib/utils";
import { useCartAssistant } from "../lib/cartAssistant/CartAssistantProvider";
import type { AssistantSuggestion as Suggestion } from "../lib/cartAssistant/types";
import { CornerDownLeft, Sparkles, Loader2Icon } from "lucide-react";
import * as React from "react";

import { useTranslation } from 'react-i18next';

/**
 * Message Suggestions component
 *
 * This component renders AI-generated suggestions for the user to quickly reply to messages.
 */

interface MessageSuggestionsContextValue {
  suggestions: Suggestion[];
  selectedSuggestionId: string | null;
  accept: (options: { suggestion: Suggestion }) => Promise<void>;
  isGenerating: boolean;
  error: Error | null;
  thread: any;
  isMac: boolean;
}

const MessageSuggestionsContext =
  React.createContext<MessageSuggestionsContextValue | null>(null);

export const useMessageSuggestions = () => {
  const context = React.useContext(MessageSuggestionsContext);
  if (!context) {
    throw new Error(
      "useMessageSuggestions must be used within MessageSuggestions",
    );
  }
  return context;
};

export interface MessageSuggestionsProps {
  initialSuggestions?: string[];
  maxSuggestions?: number;
  autoGenerate?: boolean;
}

export function MessageSuggestions({
  initialSuggestions = [],
  maxSuggestions = 3,
  autoGenerate = true,
}: MessageSuggestionsProps) {
  const { t } = useTranslation();
  const { thread, isStreaming, suggestions: allSuggestions, isGeneratingSuggestions: isGenerating, sendMessage } = useCartAssistant();

  const messages = thread?.thread?.messages ?? [];
  const suggestions = React.useMemo(() => allSuggestions.slice(0, maxSuggestions), [allSuggestions, maxSuggestions]);
  const [selectedSuggestionId, setSelectedSuggestionId] = React.useState<string | null>(null);
  const error: Error | null = null;

  const accept = React.useCallback(async ({ suggestion }: { suggestion: Suggestion }) => {
    setSelectedSuggestionId(suggestion.id);
    await sendMessage(suggestion.detailedSuggestion || suggestion.title);
  }, [sendMessage]);

  // Track when suggestions finish generating to notify parents if needed
  // or just wait for the memoized trigger in ThreadContent to handle it.
  // We'll add a small local effect to ensure visibility.
  React.useEffect(() => {
    if (!isGenerating && suggestions.length > 0) {
      // Dispatch a small window event that ScrollableMessageContainer can listen to
      window.dispatchEvent(new CustomEvent('assistant-suggestions-ready'));
    }
  }, [isGenerating, suggestions.length]);

  const [isMac, setIsMac] = React.useState(false);

  React.useEffect(() => {
    setIsMac(typeof navigator !== "undefined" && navigator.platform.toUpperCase().indexOf("MAC") >= 0);
  }, []);

  const contextValue = React.useMemo(
    () => ({
      suggestions,
      selectedSuggestionId,
      accept,
      isGenerating,
      error,
      thread,
      isMac,
    }),
    [
      suggestions,
      selectedSuggestionId,
      accept,
      isGenerating,
      error,
      thread,
      isMac,
    ],
  );

  if (isStreaming) {
    return null;
  }

  if (!messages.length && initialSuggestions.length === 0) {
    return null;
  }

  return (
    <MessageSuggestionsContext.Provider value={contextValue}>
      <TooltipProvider>
        <div className="flex flex-col gap-1 pt-2.5 px-3">
          {isGenerating && suggestions.length === 0 && (
            <div className="flex items-center gap-2 px-1 text-[11px] font-medium text-gray-400">
              <Loader2Icon className="h-3 w-3 animate-spin" />
              <span>{t('chat.generating_suggestions')}</span>
            </div>
          )}

          <div className="flex overflow-x-auto gap-2 pb-0.5 scrollbar-hide scroll-smooth flex-nowrap [mask-image:linear-gradient(to_right,black_85%,transparent)]">
            {suggestions.map((suggestion) => (
              <SuggestionItem key={suggestion.id} suggestion={suggestion} />
            ))}

            {suggestions.length === 0 &&
              initialSuggestions.map((text, index) => (
                <SuggestionItem
                  key={`initial-${index}`}
                  suggestion={{
                    id: `initial-${index}`,
                    title: text,
                    detailedSuggestion: text, // Required by SDK's accept function
                  } as any}
                />
              ))}
          </div>
        </div>
      </TooltipProvider>
    </MessageSuggestionsContext.Provider>
  );
}

function SuggestionItem({ suggestion }: { suggestion: Suggestion }) {
  const { t } = useTranslation();
  const { accept, selectedSuggestionId } = useMessageSuggestions();
  const isSelected = selectedSuggestionId === suggestion.id;

  const handleClick = async () => {
    try {
      await accept({ suggestion });
    } catch (err) {
      console.error("Failed to accept suggestion:", err);
    }
  };

  return (
    <Tooltip content={suggestion.title}>
      <button
        onClick={handleClick}
        className={cn(
          "group flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 text-[12px] font-semibold text-[#1a2b3c] rounded-full shrink-0 whitespace-nowrap transition-colors",
          "border border-gray-200 bg-white hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700",
          isSelected && "border-emerald-400 bg-emerald-50 text-emerald-700",
        )}
      >
        <Sparkles className="h-3 w-3 text-emerald-500" />
        <span>{suggestion.title}</span>
      </button>
    </Tooltip>
  );
}
